import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  HubConnection,
  HubConnectionState,
} from '@microsoft/signalr';
import { useAuthStore } from '@/auth/auth-store';
import {
  PRACTICE_HUB_URL,
  SCHEDULING_HUB_URL,
  createHubConnection,
} from './signalr-connection';
import type {
  AppointmentsDetailsResponse,
  ListAppointmentResponse,
} from '@/types/scheduling-api';
import type { CalendarView } from '@/scheduling/store/scheduling-store';

/**
 * useSchedulingRealtime — wires the calendar screen to the Scrips SignalR hubs.
 *
 * Subscriptions (mirrors Flutter's calendar_tab.dart + calendar_screen.dart):
 *   1. UpdateAppointments       on Scheduling hub
 *      → surgical merge into the cached AppointmentsDetailsResponse:
 *        - Block appt with no provider     → REMOVE matching id
 *        - Block-or-not + practice match
 *          + within current view's window  → ADD if absent, UPDATE if present
 *      → recompute waitingRoom / noShow / cancelled counts after merge
 *
 *   2. RefreshSlots             on BOTH Scheduling and Practice hubs
 *      → if the emitted practiceSetupId matches our practice, invalidate
 *        the calendar-slots query so the strip + grid re-fetches
 *
 *   3. UpdateAppointmentsCompact on Scheduling hub
 *      → no-op slot. The UA (patient) app subscribes to a slimmer payload
 *        variant; PM doesn't need it but we keep the listener attached so
 *        the backend can emit it without throwing on an unhandled method.
 *
 * Connections are created lazily once params arrive, started in an effect,
 * and stopped on unmount or param change. Token retrieval goes through
 * useAuthStore.getValidAccessToken so refreshes are picked up.
 */

interface RealtimeParams {
  /** Cache key the appointments query was registered under (the same object
   *  passed to useAppointments). The hook uses it to setQueryData. */
  appointmentsParams: unknown | null;
  /** ISO date displayed in the day view — for the date-window check. */
  selectedDateISO: string;
  /** Practice id we're currently viewing — gates RefreshSlots. */
  practiceId: string | null | undefined;
  /** Active calendar view (drives the count-recompute branch). */
  calendarView: CalendarView;
}

const APPT_CANCELLED = 'cancelled';
const APPT_NOSHOW = 'noshow';
const APPT_CHECKEDIN = 'checkedin';

export function useSchedulingRealtime({
  appointmentsParams,
  selectedDateISO,
  practiceId,
  calendarView,
}: RealtimeParams): void {
  const queryClient = useQueryClient();

  // Snapshot the latest props in refs so the SignalR handlers (which are
  // bound once per connection) always see fresh values without resubscribing
  // on every render.
  const paramsRef = useRef(appointmentsParams);
  const dateRef = useRef(selectedDateISO);
  const practiceRef = useRef(practiceId);
  const viewRef = useRef(calendarView);
  paramsRef.current = appointmentsParams;
  dateRef.current = selectedDateISO;
  practiceRef.current = practiceId;
  viewRef.current = calendarView;

  useEffect(() => {
    if (!practiceId) return;

    const getToken = () => useAuthStore.getState().getValidAccessToken();
    const schedulingHub = createHubConnection(SCHEDULING_HUB_URL, getToken);
    const practiceHub = createHubConnection(PRACTICE_HUB_URL, getToken);

    schedulingHub.on('UpdateAppointments', (...args: unknown[]) => {
      const appt = firstAppointment(args);
      if (!appt) return;
      mergeAppointmentIntoCache({
        queryClient,
        cacheKey: ['appointments', paramsRef.current] as QueryKey,
        appt,
        selectedDateISO: dateRef.current,
        practiceId: practiceRef.current,
        calendarView: viewRef.current,
      });
    });

    schedulingHub.on('UpdateAppointmentsCompact', () => {
      // Slot kept for backend parity; UA-only event, no PM behavior.
    });

    const refreshSlotsHandler = (...args: unknown[]) => {
      const id = firstString(args);
      if (id && id === practiceRef.current) {
        void queryClient.invalidateQueries({ queryKey: ['calendar-slots'] });
      }
    };
    schedulingHub.on('RefreshSlots', refreshSlotsHandler);
    practiceHub.on('RefreshSlots', refreshSlotsHandler);

    // Lifecycle that survives StrictMode's double-invoke. The cleanup runs
    // before `start()` resolves on the first pass; if we naively call
    // `stop()` mid-negotiation SignalR throws "stopped during negotiation".
    // Track a `disposed` flag and stop ONLY after start has settled (or
    // immediately if start was never reached).
    let disposed = false;

    const startSafely = async (conn: HubConnection): Promise<void> => {
      try {
        if (conn.state === HubConnectionState.Disconnected) {
          await conn.start();
        }
        // Effect was cleaned up while we were negotiating — tear down now.
        if (disposed && conn.state !== HubConnectionState.Disconnected) {
          await conn.stop();
        }
      } catch {
        // Auto-reconnect handles real failures; StrictMode-induced aborts
        // are expected and we don't surface them.
      }
    };

    void startSafely(schedulingHub);
    void startSafely(practiceHub);

    return () => {
      disposed = true;
      // Best-effort stop. If the connection is still negotiating, the
      // startSafely closure above will tear it down once start resolves.
      if (schedulingHub.state === HubConnectionState.Connected) {
        void schedulingHub.stop().catch(() => {});
      }
      if (practiceHub.state === HubConnectionState.Connected) {
        void practiceHub.stop().catch(() => {});
      }
    };
  }, [practiceId, queryClient]);
}

/**
 * SignalR hands handlers a variadic argument list. Flutter's hub passes a
 * single array, so the appointment is always the first element. Defensive
 * unwrap — accept either `[appt]` or `appt` directly.
 */
/**
 * Unwraps the appointment out of a SignalR invocation's args.
 *
 * EXPORTED because the encounter has its own appointment listener now
 * (`encounter/realtime/use-encounter-appointment-realtime.ts`, PROD-2028) and both must agree on
 * the wire shape. The .NET server sometimes wraps the payload in an array and sometimes does not —
 * Flutter reads `message.first` (`encounter_screen.dart:263`) — so this tolerance is a real
 * contract detail, not defensive noise. A second copy of it in the encounter would be free to drift.
 */
export function firstAppointment(args: unknown[]): ListAppointmentResponse | undefined {
  const head = args[0];
  if (Array.isArray(head)) return head[0] as ListAppointmentResponse | undefined;
  if (head && typeof head === 'object') return head as ListAppointmentResponse;
  return undefined;
}

function firstString(args: unknown[]): string | undefined {
  const head = args[0];
  if (Array.isArray(head)) return typeof head[0] === 'string' ? head[0] : undefined;
  if (typeof head === 'string') return head;
  return undefined;
}

interface MergeArgs {
  queryClient: ReturnType<typeof useQueryClient>;
  cacheKey: QueryKey;
  appt: ListAppointmentResponse & { isBlock?: boolean };
  selectedDateISO: string;
  practiceId: string | null | undefined;
  calendarView: CalendarView;
}

/**
 * Surgical equivalent of Flutter calendar_tab.dart:847-980. Mutates the
 * cached AppointmentsDetailsResponse via setQueryData with one of:
 *   - REMOVE  (block appt with no provider)
 *   - ADD     (matches practice + window + not already cached)
 *   - UPDATE  (matches practice + window + already cached, same date)
 *   - REMOVE  (cached but the new copy crosses out of the displayed day)
 *
 * Counts are recomputed from the resulting list — Flutter does this for
 * Day (index 0) and the Room/Doctor views (3, 4) after merging. Week and
 * Month aren't count-driven in the original code.
 */
function mergeAppointmentIntoCache(args: MergeArgs): void {
  const { queryClient, cacheKey, appt, selectedDateISO, practiceId, calendarView } = args;

  queryClient.setQueryData<AppointmentsDetailsResponse | undefined>(
    cacheKey,
    (prev) => {
      if (!prev) return prev;
      const appointments = prev.appointments ?? [];
      const apptId = appt.id;
      const idx = appointments.findIndex((a) => a.id === apptId);

      // Block appt + no provider → remove.
      const isBlock = Boolean(
        (appt as { isBlock?: boolean }).isBlock,
      );
      const providerId = providerIdOf(appt);
      if (isBlock && !providerId) {
        if (idx < 0) return prev;
        return withCounts({
          ...prev,
          appointments: removeAt(appointments, idx),
        }, calendarView);
      }

      // Practice mismatch (non-block) → ignore, but if it was previously
      // cached with a different practice we still drop it.
      const apptPracticeId = (appt as { practice?: { id?: string } }).practice?.id;
      const practiceMatches = isBlock
        ? true
        : Boolean(practiceId && apptPracticeId && apptPracticeId === practiceId);
      if (!practiceMatches) {
        if (idx < 0) return prev;
        return withCounts({
          ...prev,
          appointments: removeAt(appointments, idx),
        }, calendarView);
      }

      // Date-window check. Flutter rule:
      //   index in {Month, Room} → no window check (full month / room)
      //   else                    → appt.start within ±1 day of selectedDate
      const inWindow =
        calendarView === 'Month' || calendarView === 'ByRoom'
          ? true
          : withinOneDay(appt.start, selectedDateISO);

      if (!inWindow) {
        if (idx < 0) return prev;
        return withCounts({
          ...prev,
          appointments: removeAt(appointments, idx),
        }, calendarView);
      }

      // ADD (not yet cached) — Flutter writes immediately when the existing
      // .where(id).length is 0. Our equivalent is idx < 0.
      if (idx < 0) {
        return withCounts({
          ...prev,
          appointments: [...appointments, appt],
        }, calendarView);
      }

      // Already cached. If the new copy crosses out of the displayed day,
      // remove. Otherwise, replace in-place.
      const sameDay = sameDayISO(appt.start, selectedDateISO);
      if (calendarView === 'Day' && !sameDay) {
        return withCounts({
          ...prev,
          appointments: removeAt(appointments, idx),
        }, calendarView);
      }

      const nextList = appointments.slice();
      nextList[idx] = appt;
      return withCounts({ ...prev, appointments: nextList }, calendarView);
    },
  );
}

function providerIdOf(a: ListAppointmentResponse): string | undefined {
  if (!a.participant) return undefined;
  for (const p of a.participant) {
    const ref = p.referenceType?.toLowerCase();
    if (p.provider?.id) return p.provider.id;
    if ((ref === 'practitioner' || ref === 'provider') && p.reference) {
      return p.reference;
    }
  }
  return undefined;
}

function removeAt<T>(list: T[], i: number): T[] {
  const out = list.slice();
  out.splice(i, 1);
  return out;
}

function sameDayISO(startISO: string | undefined, refISO: string): boolean {
  if (!startISO) return false;
  const a = new Date(startISO);
  const b = new Date(refISO);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function withinOneDay(startISO: string | undefined, refISO: string): boolean {
  if (!startISO) return false;
  const a = new Date(startISO).getTime();
  const b = new Date(refISO).getTime();
  return Math.abs(a - b) <= 24 * 60 * 60 * 1000;
}

/**
 * Recompute waitingRoom / noShow / cancelled summary counts from the
 * appointments list. Flutter does this for the Day, Room, and Doctor views;
 * Week and Month aren't count-driven in the original code.
 */
function withCounts(
  response: AppointmentsDetailsResponse,
  view: CalendarView,
): AppointmentsDetailsResponse {
  if (view !== 'Day' && view !== 'ByRoom' && view !== 'ByDoctor') return response;
  let waitingRoom = 0;
  let noShow = 0;
  let cancelled = 0;
  for (const a of response.appointments ?? []) {
    const code = a.status?.code?.toLowerCase();
    if (code === APPT_CHECKEDIN) waitingRoom++;
    else if (code === APPT_NOSHOW) noShow++;
    else if (code === APPT_CANCELLED) cancelled++;
  }
  return { ...response, waitingRoom, noShow, cancelled };
}
